import { ScheduledPick } from 'common/models';
import { BinRequest } from 'common/models/binRequest';
import { DateWithoutTime } from 'common/models/dateType';
import { PackHouse, PickPlan } from 'common/models/packHouse';
import { PickScheduleData } from 'common/models/pickSchedule';
import {
  RemovePlanRowData,
  UpdateBinReqData,
} from 'features/pick-plan-views/hooks/usePickPlanActions';
import { cmsApi } from '../cmsApi';
import {
  EditScheduledPickPayload,
  ScheduledPickPayload,
} from '../dto/pickSchedulingApi.types';
import { shouldForceRefetch } from '../utils/handleExpiredSession';

export type DaySummaryDto = {
  id: number;
  scheduledTotal: number;
  pickedTotal: number;
  isApproved: boolean;
};

const packHouseBaseUrl = '/pack-house';
const pickScheduleBaseUrl = '/harvest-data';
const scheduledPickPath = 'scheduled-pick';

export const withScheduleEndpoints = cmsApi.injectEndpoints({
  endpoints: builder => ({
    getPackHouses: builder.query<PackHouse[], void>({
      query: () => ({
        url: `${packHouseBaseUrl}`,
      }),
      providesTags: (_, err) => (err ? [] : ['PackingHouses']),
      forceRefetch: shouldForceRefetch,
    }),

    getPickPlan: builder.query<
      PickPlan,
      { packHouseId: number; startDate: DateWithoutTime }
    >({
      query: ({ packHouseId, startDate }) => ({
        url: `${packHouseBaseUrl}/${packHouseId}/pick-plan/${startDate}`,
      }),
      providesTags: (_, err, arg) =>
        err ? [] : [{ type: 'PickPlan', id: arg.packHouseId }],
      forceRefetch: shouldForceRefetch,
    }),

    updateBinRequest: builder.mutation<BinRequest, UpdateBinReqData>({
      query: ({
        packHouseId,
        binId,
        varietyId,
        market,
        sizeId,
        bins,
        date,
      }) => ({
        url: `${packHouseBaseUrl}/${packHouseId}/bin-request`,
        method: 'PUT',
        body: {
          ...(binId ? { binId } : {}),
          date,
          varietyId,
          market,
          sizeId,
          bins,
        },
      }),
      invalidatesTags: (_, err, arg) =>
        err ? [] : [{ type: 'PickPlan', id: arg.packHouseId }],
    }),

    deletePlanRow: builder.mutation<void, RemovePlanRowData>({
      query: ({ startDate, packHouseId, variety, market, size }) => ({
        url: `${packHouseBaseUrl}/${packHouseId}/pick-plan-row`,
        method: 'DELETE',
        params: { startDate, variety, market, size },
      }),
      invalidatesTags: (_, err, arg) =>
        err ? [] : [{ type: 'PickPlan', id: arg.packHouseId }],
    }),

    getPickSchedule: builder.query<
      PickScheduleData,
      { date: string; packHouseId: number }
    >({
      query: ({ date, packHouseId }) => ({
        url: `${pickScheduleBaseUrl}/${scheduledPickPath}/${date}/${packHouseId}`,
        method: 'GET',
      }),
      // Don't provide tags here, as we always want to fetch pick schedules.
      forceRefetch: () => true,
    }),

    getPickScheduleSummary: builder.query<DaySummaryDto, string>({
      query: date => ({
        url: `${pickScheduleBaseUrl}/day-summary/${date}`,
        method: 'GET',
      }),
      providesTags: ['DayScheduleSummary'],
      // Always get the latest summary data.
      forceRefetch: () => true,
    }),

    approveSchedule: builder.mutation<
      void,
      { id: number; isApproved: boolean }
    >({
      query: body => ({
        url: `${pickScheduleBaseUrl}/schedule-approval`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['DayScheduleSummary']),
    }),

    schedulePick: builder.mutation<void, ScheduledPickPayload>({
      query: scheduledPick => ({
        url: `${pickScheduleBaseUrl}/${scheduledPickPath}`,
        method: 'POST',
        body: scheduledPick,
      }),
      // Don't provide/invalidate tags here, as pick schedules are always fetched.
    }),

    getScheduledPickById: builder.query<ScheduledPick, { id: number }>({
      query: ({ id }) => ({
        url: `${pickScheduleBaseUrl}/${scheduledPickPath}/${id}`,
        method: 'GET',
      }),
      providesTags: (_, err, arg) =>
        err ? [] : [{ type: 'ScheduledPick', id: arg.id }],
      forceRefetch: shouldForceRefetch,
    }),

    editScheduledPick: builder.mutation<void, EditScheduledPickPayload>({
      query: ({ id, schedId, ...payload }) => ({
        url: `${pickScheduleBaseUrl}/${scheduledPickPath}/${schedId}/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, err, arg) =>
        err ? [] : [{ type: 'ScheduledPick', id: arg.id }],
    }),

    deletePickSchedule: builder.mutation<void, number>({
      query: pickId => ({
        url: `${pickScheduleBaseUrl}/${scheduledPickPath}/${pickId}`,
        method: 'DELETE',
      }),
      // No need to invalidate tags here, as pick schedules are always fetched.
    }),
  }),
});

export const {
  useGetPackHousesQuery,
  useGetPickPlanQuery,
  useUpdateBinRequestMutation,
  useDeletePlanRowMutation,
  useGetPickScheduleQuery,
  useGetPickScheduleSummaryQuery,
  useGetScheduledPickByIdQuery,
  useDeletePickScheduleMutation,
  useSchedulePickMutation,
  useEditScheduledPickMutation,
  useApproveScheduleMutation,
} = withScheduleEndpoints;
